'use client';

import React from 'react';

import { LinkToServices } from '@/components/LinkToServices';
import { Media } from '@/components/Media';
import { ModalForm } from '@/components/ModalForm';
import { Svg } from '@ui/svg';
import { Button } from '@ui/button';
import type {
  Misc,
  Media as MediaType,
  FeaturesGlobalWidget,
} from '@/payload-types';

import doctors from './assets/doctors.webp';

export const HeroMainPage: React.FC<{
  description: string;
  image: MediaType;
  discounts: FeaturesGlobalWidget['value'];
  features: FeaturesGlobalWidget['value'];
  misc: Misc;
}> = ({ description, image, features, discounts, misc }) => {
  const { phones, address } = misc.refs;

  return (
    <section
      className="v-margin !mt-0 lg:overflow-hidden"
      data-name="HeroMainPage"
    >
      <div className="container">
        <div className="flex gap-6 lg:flex-col">
          <div className="w-full max-w-[326px] pt-8 md:pt-0 lg:max-w-none lg:pb-2">
            <div className="flex-center h7 text-blue mb-5 h-8 w-fit gap-2 rounded-full border border-gray-500 pr-[13px] pl-[10px] md:mb-4 md:h-6 md:pr-3 md:pl-[9px]">
              <Svg id="location" width="14" height="18" />
              {address?.city}
            </div>

            <h1 className="relative z-10 mb-[27px] text-[54px] leading-[80px] font-medium tracking-tight uppercase md:text-[32.5px] md:leading-[41px]">
              <div className="w-fit rounded-[24px] bg-[#FAFAFA] pr-[29px] md:pr-0">
                Психиатрическая
              </div>
              <div className="relative flex w-[432px] items-center gap-2 rounded-b-[24px] bg-[#FAFAFA] whitespace-nowrap lg:w-auto">
                клиника
                <img
                  className="w-[122px] flex-none md:w-[92px]"
                  src={doctors.src}
                />
                <Svg
                  className="absolute top-0 left-[100%] rotate-90"
                  id="corner"
                />
                <Svg
                  className="absolute top-full right-[58px] rotate-90"
                  id="corner"
                />
              </div>
              <div className="text-blue rounded-[24px] bg-[#FAFAFA]">
                спасение
              </div>
            </h1>

            <div className="mb-[35px] border-t border-gray-500 pt-[30px] lg:mb-[13px] lg:pt-[26px]">
              <div className="h7 md:max-w-[312px]">{description}</div>
            </div>

            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-3">
              <ModalForm
                toggleStyle="grow lg:max-w-[200px]"
                toggle={<Button className="w-full">Заказать звонок</Button>}
              />
              {phones?.[0] && (
                <a
                  className="h6 link-secondary text-blue mx-auto md:m-0"
                  href={`tel:${phones[0].phone}`}
                >
                  {phones[0].phone}
                </a>
              )}
            </div>
          </div>

          <div className="relative w-full max-w-[634px] lg:max-w-none">
            <Media resource={image} imgClassName="corners" priority />
            <div className="absolute bottom-0 left-0 hidden w-[168px] rounded-tr-[24px] bg-[#fafafa] pt-4 lg:block">
              <LinkToServices />
              <Svg id="corner" className="absolute bottom-[100%] left-0" />
              <Svg id="corner" className="absolute bottom-0 left-[100%]" />
            </div>
          </div>

          <div className="w-full max-w-[368px] pt-8 md:pt-0 lg:max-w-none">
            <LinkToServices className="mb-8 lg:hidden" />

            {discounts && discounts.length > 0 && (
              <div className="grid grid-cols-1 gap-4 md:gap-2">
                {discounts.map((i) => (
                  <div
                    className="corners relative flex aspect-[368/208] flex-col overflow-hidden bg-[#D1DEF3] p-2 mix-blend-darken md:aspect-[343/192]"
                    key={i.id}
                  >
                    <Media resource={i.image} fill priority />
                    <div className="corners-md h7 mt-auto ml-auto w-full max-w-[288px] bg-white/40 p-4 backdrop-blur md:max-w-[230px] md:px-[14px] md:py-3">
                      {i.title}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {features && features.length > 0 && (
          <div className="mt-14 grid grid-cols-4 gap-4 md:mt-12 md:gap-2 lg:grid-cols-1">
            {features.map((i) => (
              <div
                className="h7 flex items-center gap-4 rounded-full border border-gray-500 p-[15px] lg:p-[7px]"
                key={i.id}
              >
                <Media className="flex-none" resource={i.image} />
                <div>{i.title}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
